import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { PROFILE } from "../data/content";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.5 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden">
      {/* spotlight backdrop */}
      <div className="absolute inset-0 spotlight animate-spotlight-pulse pointer-events-none" />
      <div className="absolute top-1/3 -left-40 h-[500px] w-[500px] rounded-full bg-gold/5 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-16 min-h-screen grid lg:grid-cols-12 items-center gap-10">
        {/* Left: kinetic type */}
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.div
            data-testid="hero-eyebrow"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="flex items-center gap-3 text-gold text-xs sm:text-sm uppercase tracking-[0.35em] mb-8"
          >
            <span className="h-px w-10 bg-gold/60" />
            {PROFILE.title} · {PROFILE.company}
          </motion.div>

          <h1 data-testid="hero-heading" className="font-serif font-light leading-[1.0] tracking-tight text-cream text-5xl sm:text-7xl lg:text-[5.5rem]">
            {["Building brands.", "Engineering", "growth."].map((line, i) => (
              <span key={i} className="line-mask -mb-[0.1em]">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  className="block"
                >
                  {i === 2 ? (
                    <span className="italic text-gold-gradient">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9 }}
            className="mt-8 max-w-xl text-cream/60 text-base sm:text-lg leading-relaxed"
          >
            {PROFILE.tagline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => document.querySelector("#schedule")?.scrollIntoView({ behavior: "smooth" })}
              className="px-8 py-4 rounded-full bg-gold text-charcoal font-semibold hover:bg-gold-light transition-all hover:scale-[1.03]"
            >
              Book a consult
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })}
              className="px-8 py-4 rounded-full border border-white/15 text-cream/90 hover:border-gold hover:text-gold transition-all"
            >
              How I help
            </button>
          </motion.div>
        </motion.div>

        {/* Right: clipped headshot with parallax */}
        <div className="lg:col-span-5 relative z-10 flex justify-center lg:justify-end">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-[300px] sm:w-[380px] lg:w-[420px]"
          >
            <div className="absolute -inset-4 border border-gold/30 rounded-[2rem] translate-x-4 translate-y-4" />
            <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-b from-charcoal-700 to-charcoal-900 aspect-[3/4]">
              <motion.img
                style={{ y: imgY, scale: imgScale }}
                src={PROFILE.heroImage}
                alt={PROFILE.name}
                data-testid="hero-portrait"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900/70 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-charcoal-800 border border-white/10 rounded-2xl px-5 py-4 backdrop-blur-xl">
              <p className="font-serif text-3xl text-gold leading-none">5–10x</p>
              <p className="text-[11px] uppercase tracking-widest text-cream/50 mt-1">Marketing ROI</p>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-cream/40"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown size={16} className="animate-bounce text-gold" />
      </motion.div>
    </section>
  );
};
